/**
 * Global Artifacts & Evidence Ingestion – String Constants
 *
 * Single source of truth for all artifact enum values.
 * Prevents drift between DB constraints, upload logic, and UI.
 */

// --- Artifact types ---
export const ARTIFACT_TYPES = {
  PHOTO: 'photo',
  VIDEO: 'video',
  PDF: 'pdf',
  SCREENSHOT: 'screenshot',
  SCAN_REPORT: 'scan_report',
  SCOPE_CAPTURE: 'scope_capture',
  LINK: 'link',
  OTHER: 'other',
} as const;

export type ArtifactType = (typeof ARTIFACT_TYPES)[keyof typeof ARTIFACT_TYPES];

// --- Storage kinds ---
export const STORAGE_KINDS = {
  UPLOADED_BLOB: 'uploaded_blob',
  EXTERNAL_LINK: 'external_link',
  VENDOR_REF: 'vendor_ref',
} as const;

export type StorageKind = (typeof STORAGE_KINDS)[keyof typeof STORAGE_KINDS];

// --- Sources ---
export const ARTIFACT_SOURCES = {
  PWA_UPLOAD: 'pwa_upload',
  SHARE_SHEET: 'share_sheet',
  LINK_CAPTURE: 'link_capture',
  EMAIL_INBOX: 'email_inbox',
  VENDOR_CONNECTOR: 'vendor_connector',
} as const;

export type ArtifactSource = (typeof ARTIFACT_SOURCES)[keyof typeof ARTIFACT_SOURCES];

// --- Link entity types ---
export const LINK_ENTITY_TYPES = {
  TICKET: 'ticket',
  WORKFLOW_COMPLAINT: 'workflow_complaint',
  WORKFLOW_NOTE: 'workflow_note',
  WORKFLOW_INSPECTION: 'workflow_inspection',
  WORKFLOW_DIAGNOSTIC: 'workflow_diagnostic',
  JOB: 'job',
  OCIS_EVENT: 'ocis_event',
  VEHICLE: 'vehicle',
  TECHNICIAN: 'technician',
  INBOX_ITEM: 'inbox_item',
  OPTIBAY_SESSION_EVIDENCE: 'optibay_session_evidence',
} as const;

export type LinkEntityType = (typeof LINK_ENTITY_TYPES)[keyof typeof LINK_ENTITY_TYPES];

/**
 * Entity types whose entity_id is NOT a UUID.
 * The DB trigger skips UUID validation for these.
 * Keep this tightly scoped – only `ticket` for now.
 */
export const NON_UUID_ENTITY_TYPES: readonly string[] = [
  LINK_ENTITY_TYPES.TICKET,
] as const;

// --- Link roles ---
export const LINK_ROLES = {
  DIAGNOSTIC_EVIDENCE: 'diagnostic_evidence',
  INSPECTION_EVIDENCE: 'inspection_evidence',
  COMPLAINT_SUPPORT: 'complaint_support',
  NOTE_ATTACHMENT: 'note_attachment',
  REFERENCE: 'reference',
} as const;

export type LinkRole = (typeof LINK_ROLES)[keyof typeof LINK_ROLES];

// --- Artifact status ---
export const ARTIFACT_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DELETED: 'deleted',
} as const;

export type ArtifactStatus = (typeof ARTIFACT_STATUS)[keyof typeof ARTIFACT_STATUS];

// --- MIME type to artifact type mapping ---
export function inferArtifactType(mimeType: string): ArtifactType {
  if (mimeType.startsWith('image/')) return ARTIFACT_TYPES.PHOTO;
  if (mimeType.startsWith('video/')) return ARTIFACT_TYPES.VIDEO;
  if (mimeType === 'application/pdf') return ARTIFACT_TYPES.PDF;
  return ARTIFACT_TYPES.OTHER;
}
